import React, { useEffect, useRef, useState } from "react";
import { useAction } from "convex/react";
import { useUser } from "@clerk/clerk-react";
import { api } from "../convex";
import toast from "react-hot-toast";

// Automatically sync subscription after returning from Polar portal
// without relying on URL params. We set a localStorage flag before
// redirecting to the portal, and check it here on load.
const FLAG_KEY = "portal_sync_pending";
const MAX_AGE_MS = 15 * 60 * 1000; // 15 minutes
const MAX_ATTEMPTS = 3;

const AutoPortalSync = () => {
  const { user, isSignedIn } = useUser();
  const syncFromPolar = useAction(api.actions.payments.syncSubscriptionFromPolar);
  const [started, setStarted] = useState(false);
  const retryTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!isSignedIn || !user || started) return;

    // Detect Polar customer portal session token in URL (if present)
    const params = new URLSearchParams(window.location.search);
    const hasCustomerSessionToken = params.has("customer_session_token");

    // Detect our local flag set before opening the portal
    const raw = localStorage.getItem(FLAG_KEY);
    let shouldSync = hasCustomerSessionToken;
    let payload = null;

    if (raw) {
      try {
        payload = JSON.parse(raw);
      } catch {
        payload = { at: Number(raw) };
      }
      const at = Number(payload?.at);
      if (Number.isFinite(at) && Date.now() - at < MAX_AGE_MS) {
        shouldSync = true;
      } else {
        // stale flag; clear it
        localStorage.removeItem(FLAG_KEY);
      }
    }

    if (!shouldSync) return;

    const attemptSync = async () => {
      setStarted(true);
      const attempts = Number(payload?.attempts ?? 0);

      try {
        const res = await syncFromPolar({ userId: user.id });
        if (res?.success) {
          toast.success(`Subscription synced: ${res.billingCycle}`);
          // Clear flag and URL params
          localStorage.removeItem(FLAG_KEY);
          const url = new URL(window.location.href);
          url.searchParams.delete("customer_session_token");
          window.history.replaceState({}, document.title, url.toString());
          setStarted(false);
          return;
        }
        throw new Error(res?.error || "Failed to sync subscription");
      } catch (e) {
        const msg = String(e?.message || e || "");
        const retriable = /Connection lost while action was in flight|network|ECONN|ENOTFOUND|timed out/i.test(msg);
        const nextAttempts = attempts + 1;
        if (retriable && nextAttempts < MAX_ATTEMPTS) {
          try {
            localStorage.setItem(FLAG_KEY, JSON.stringify({ at: Date.now(), reason: 'portal', attempts: nextAttempts }));
          } catch {}
          retryTimer.current = setTimeout(() => {
            setStarted(false); // allow re-run
          }, 1000 * nextAttempts); // backoff: 1s, 2s
          return;
        }
        // Final failure: clear flag and notify
        try { localStorage.removeItem(FLAG_KEY); } catch {}
        toast.error(msg || 'Failed to sync subscription');
        // Clean URL token param if present
        const url = new URL(window.location.href);
        url.searchParams.delete("customer_session_token");
        window.history.replaceState({}, document.title, url.toString());
        setStarted(false);
      }
    };

    // Kick off
    attemptSync();
  }, [isSignedIn, user, syncFromPolar, started]);

  return null;
};

export default AutoPortalSync;